import { Button } from '@/components/Button/Button'
import { cn } from '@/lib/utils'
import { ApiPrice } from 'sprites-types/api/prices'
import { ApiUserQuota } from 'sprites-types/api/user-quota'
import { PriceDisplay } from './PriceDisplay'
import { Plan } from './constants'

interface PlanCardProps {
  plan: Plan
  prices: Record<string, ApiPrice[]>
  billingInterval: 'month' | 'year'
  quota: ApiUserQuota | undefined
  onButtonClick: (plan: Plan) => void
  isLoggedIn: boolean
  minimal?: boolean
}

export const PlanCard = ({
  plan,
  prices,
  billingInterval,
  quota,
  onButtonClick,
  isLoggedIn,
  minimal,
}: PlanCardProps) => {
  const getButtonText = () => {
    if (plan.id === 'business') return 'Contact Us'
    if (!isLoggedIn) return 'Join now'
    if (quota?.subscription?.productId === plan.stripeProductId) return 'Current Plan'
    return `Switch to ${plan.name}`
  }

  const cardHeightClassName = minimal ? 'relative h-32' : 'relative h-52'

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl border border-gray-200 bg-white px-6 py-8 shadow-lg transition-all duration-300 ease-out`}
    >
      {/* Accent gradient line for highlights */}
      {plan.isPopular && (
        <div
          className="absolute left-0 top-0 h-1 w-full rounded-t-2xl"
          style={{ background: 'linear-gradient(90deg, rgb(253,149,67), rgb(249,150,205))' }}
        />
      )}
      <div className={'relative flex h-full flex-col transition-all'}>
        <div className={cardHeightClassName}>
          <h3 className="font-radio-grotesk mb-2 text-2xl text-gray-900">{plan.name}</h3>
          <div className="text-bottom mt-4 flex flex-row items-end gap-x-1">
            <PriceDisplay plan={plan} prices={prices} billingInterval={billingInterval} />
          </div>
          {!minimal && <p className="font-inter mt-2 text-base text-gray-600">{plan.tagline}</p>}
        </div>
        <Button
          onClick={() => {
            onButtonClick(plan)
          }}
          className={cn(
            'font-radio-grotesk mb-8 w-full rounded-xl border border-gray-300 bg-white font-bold text-gray-900 shadow-sm transition-all duration-200 hover:shadow-md',
            isLoggedIn && quota?.subscription?.productId === plan.stripeProductId
              ? 'pointer-events-none opacity-20'
              : ''
          )}
          disabled={
            isLoggedIn && (!plan.id || quota?.subscription?.productId === plan.stripeProductId)
          }
        >
          {getButtonText()}
        </Button>
        <p className="font-inter mb-2 text-base text-gray-700">{plan.description}</p>
        <div className={`flex flex-col ${plan.description ? '' : '-mt-2'}`}>
          {plan.features.map((feature: string, idx: number) => (
            <p
              key={`${plan.id}-feature-${idx}`}
              className="font-inter mt-2 text-base text-gray-600"
            >
              {`✓ ${feature}`}
            </p>
          ))}
        </div>
      </div>
    </div>
  )
}
